import { ProcessedHtmlObject } from "@/types/templateProcessing";

interface PlaceholderInfo {
  fieldId: string;
  placeholderText: string;
  displayName: string;
}

/**
 * Formats a placeholder text into a user-friendly display name
 * by removing field_ prefix, converting underscores to spaces,
 * and properly capitalizing each word
 */
export const formatDisplayName = (text: string): string => {
  // Remove field_ prefix if it exists
  const withoutPrefix = text.replace(/^field_/, '');
  
  // Convert underscores or hyphens to spaces
  const withSpaces = withoutPrefix.replace(/[_-]/g, ' ');
  
  // Capitalize each word
  return withSpaces.split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Generates a meaningful field ID from placeholder text
 * Now creates more descriptive IDs instead of generic numbered ones
 */
export const generateFieldId = (text: string, existingIds: Set<string>): string => {
  // Clean the text and convert to lowercase
  let baseId = text.toLowerCase()
    .trim()
    .replace(/\s+/g, '_')       // Replace spaces with underscores
    .replace(/[^\w_-]/g, '')    // Remove any non-alphanumeric characters except underscores and hyphens
    .replace(/^_+|_+$/g, '')    // Remove leading/trailing underscores
    .replace(/_+/g, '_');       // Replace multiple underscores with single underscore
  
  // If the baseId is empty or too short, generate a meaningful default
  if (!baseId || baseId.length < 2) {
    baseId = 'field_value';
  }
  
  // Add field_ prefix for consistency
  let fieldId = `field_${baseId}`;
  
  // Ensure uniqueness by adding a counter if necessary
  let counter = 1;
  let uniqueId = fieldId;
  
  while (existingIds.has(uniqueId)) {
    uniqueId = `${fieldId}_${counter}`;
    counter++;
  }
  
  return uniqueId;
};

/**
 * Enhanced function to extract meaningful text from placeholder context
 */
const extractMeaningfulText = (placeholderText: string, context: string = ''): string => {
  // If the placeholder text is meaningful, use it
  if (placeholderText && placeholderText.length > 2 && !placeholderText.match(/^[_\s\.\-]+$/)) {
    return placeholderText;
  }
  
  // Try to extract meaningful context from surrounding text
  const contextWords = context.toLowerCase()
    .replace(/[^\w\s]/g, ' ')
    .split(/\s+/)
    .filter(word => word.length > 2)
    .filter(word => !['the', 'and', 'for', 'are', 'but', 'not', 'you', 'all', 'can', 'had', 'her', 'was', 'one', 'our', 'out', 'day', 'get', 'has', 'him', 'his', 'how', 'man', 'new', 'now', 'old', 'see', 'two', 'way', 'who', 'boy', 'did', 'its', 'let', 'put', 'say', 'she', 'too', 'use'].includes(word));
  
  if (contextWords.length > 0) {
    return contextWords.slice(0, 3).join('_');
  }
  
  return 'field_value';
};

export const processUploadedTemplate = (html: string): { 
  processedHtml: string; 
  placeholders: string[];
  displayNames: Record<string, string>;
} => {
  // Remove any <style>...</style> blocks from the raw HTML
  const styleBlockRegex = /<style[\s\S]*?>[\s\S]*?<\/style>/gi;
  let cleanHtml = html.replace(styleBlockRegex, '');
  
  // Create a DOM parser to properly handle HTML elements
  const parser = new DOMParser();
  const doc = parser.parseFromString(cleanHtml, 'text/html');
  
  // Add template classes to elements
  doc.querySelectorAll('table').forEach(table => {
    table.classList.add('template-table');
    table.querySelectorAll('tr').forEach(tr => tr.classList.add('template-table-row'));
    table.querySelectorAll('th').forEach(th => th.classList.add('template-table-header'));
    table.querySelectorAll('td').forEach(td => td.classList.add('template-table-cell'));
  });
  
  doc.querySelectorAll('p').forEach(p => p.classList.add('template-paragraph'));
  doc.querySelectorAll('ul').forEach(ul => ul.classList.add('template-ul'));
  doc.querySelectorAll('ol').forEach(ol => ol.classList.add('template-ol'));
  doc.querySelectorAll('li').forEach(li => li.classList.add('template-li'));
  doc.querySelectorAll('h1').forEach(h1 => h1.classList.add('template-heading-h1'));
  doc.querySelectorAll('h2').forEach(h2 => h2.classList.add('template-heading-h2'));
  doc.querySelectorAll('h3').forEach(h3 => h3.classList.add('template-heading-h3'));
  doc.querySelectorAll('h4,h5,h6').forEach(h => h.classList.add('template-heading-h4'));
  
  cleanHtml = doc.body.innerHTML;
  
  // Match text within square brackets [Placeholder]
  const placeholderRegex = /\[([^\]]*)\]/g;
  const placeholders = new Set<string>();
  const placeholderMap = new Map<string, PlaceholderInfo>();
  const displayNames: Record<string, string> = {};
  
  // First pass: collect all placeholders with context
  let match;
  const htmlCopy = cleanHtml;
  
  while ((match = placeholderRegex.exec(htmlCopy)) !== null) {
    const placeholderText = match[1].trim();
    const matchStart = match.index;
    const matchEnd = match.index + match[0].length;
    
    // Extract context around the placeholder for better field naming
    const contextBefore = htmlCopy.substring(Math.max(0, matchStart - 100), matchStart);
    const contextAfter = htmlCopy.substring(matchEnd, Math.min(htmlCopy.length, matchEnd + 100));
    const fullContext = contextBefore + ' ' + contextAfter;
    
    // Generate meaningful text for the field ID
    const meaningfulText = extractMeaningfulText(placeholderText, fullContext);
    
    // Create a field ID based on the meaningful text
    const fieldId = generateFieldId(meaningfulText, placeholders);
    placeholders.add(fieldId);
    
    // Generate a user-friendly display name
    const displayName = formatDisplayName(meaningfulText);
    displayNames[fieldId] = displayName;
    
    placeholderMap.set(match[0], { 
      fieldId, 
      placeholderText: placeholderText || meaningfulText,
      displayName
    });
  }

  // Replace placeholders in the content
  let processedHtml = cleanHtml;
  placeholderMap.forEach((info, originalText) => {
    const regex = new RegExp(escapeRegExp(originalText), 'g');
    processedHtml = processedHtml.replace(
      regex,
      `<input class="placeholder-input" data-placeholder="${info.fieldId}" placeholder="${info.displayName}" type="text">`
    );
  });

  // Only add the .template-content wrapper, no <style> tag
  processedHtml = `
    <div class="template-content">
      ${processedHtml}
    </div>
  `;

  return { 
    processedHtml, 
    placeholders: Array.from(placeholders),
    displayNames
  };
};

// Helper function to escape special characters in regex
function escapeRegExp(string: string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Detects placeholders in user-added content and converts them to input fields
 */
export const processUserAddedContent = (htmlContent: string): {
  processedHtml: string;
  newPlaceholders: string[];
  displayNames: Record<string, string>;
} => {
  // Match text within square brackets [Placeholder]
  const placeholderRegex = /\[([^\]]*)\]/g;
  const newPlaceholders = new Set<string>();
  const placeholderMap = new Map<string, PlaceholderInfo>();
  const displayNames: Record<string, string> = {};
  
  let match;
  let processedHtml = htmlContent;
  
  while ((match = placeholderRegex.exec(htmlContent)) !== null) {
    const placeholderText = match[1].trim();
    
    // Generate meaningful text for the field ID
    const meaningfulText = extractMeaningfulText(placeholderText);
    
    // Create a field ID based on the meaningful text
    const fieldId = generateFieldId(meaningfulText, newPlaceholders);
    newPlaceholders.add(fieldId);
    
    // Generate a user-friendly display name
    const displayName = formatDisplayName(meaningfulText);
    displayNames[fieldId] = displayName;
    
    placeholderMap.set(match[0], { 
      fieldId, 
      placeholderText: placeholderText || meaningfulText,
      displayName
    });
  }
  
  // Replace placeholders in the content
  placeholderMap.forEach((info, originalText) => {
    const regex = new RegExp(escapeRegExp(originalText), 'g');
    processedHtml = processedHtml.replace(
      regex,
      `<input class="placeholder-input" data-placeholder="${info.fieldId}" placeholder="${info.displayName}" type="text">`
    );
  });

  // Add template classes to the new content
  const parser = new DOMParser();
  const doc = parser.parseFromString(processedHtml, 'text/html');
  
  doc.querySelectorAll('p').forEach(p => p.classList.add('template-paragraph'));
  doc.querySelectorAll('table').forEach(table => {
    table.classList.add('template-table');
    table.querySelectorAll('tr').forEach(tr => tr.classList.add('template-table-row'));
    table.querySelectorAll('th').forEach(th => th.classList.add('template-table-header'));
    table.querySelectorAll('td').forEach(td => td.classList.add('template-table-cell'));
  });
  
  processedHtml = doc.body.innerHTML;

  return { 
    processedHtml, 
    newPlaceholders: Array.from(newPlaceholders),
    displayNames
  };
};

/**
 * Sanitizes user-added content to prevent XSS and maintain formatting
 */
export const sanitizeUserContent = (htmlContent: string): string => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(htmlContent, 'text/html');
  
  // Remove potentially harmful elements and attributes
  const allowedTags = ['p', 'br', 'strong', 'em', 'u', 'span', 'input'];
  const allowedAttributes = ['class', 'data-placeholder', 'placeholder', 'type'];
  
  const walker = doc.createTreeWalker(
    doc.body || doc.documentElement,
    NodeFilter.SHOW_ELEMENT,
    null
  );
  
  const elementsToRemove: Element[] = [];
  let node = walker.nextNode() as Element;
  
  while (node) {
    if (!allowedTags.includes(node.tagName.toLowerCase())) {
      elementsToRemove.push(node);
    } else {
      // Remove disallowed attributes
      const attributesToRemove: string[] = [];
      for (let i = 0; i < node.attributes.length; i++) {
        const attr = node.attributes[i];
        if (!allowedAttributes.includes(attr.name)) {
          attributesToRemove.push(attr.name);
        }
      }
      attributesToRemove.forEach(attr => node.removeAttribute(attr));
    }
    node = walker.nextNode() as Element;
  }
  
  // Remove disallowed elements
  elementsToRemove.forEach(element => {
    element.parentNode?.removeChild(element);
  });
  
  return doc.body ? doc.body.innerHTML : doc.documentElement.innerHTML;
};

/**
 * Processes modified template content with user additions
 */
export const processModifiedTemplate = (
  modifiedHtml: string,
  existingPlaceholders: string[] = [],
  existingDisplayNames: Record<string, string> = {}
): {
  processedHtml: string;
  allPlaceholders: string[];
  allDisplayNames: Record<string, string>;
} => {
  // Sanitize the modified content first
  const sanitizedHtml = sanitizeUserContent(modifiedHtml);
  
  // Process any new placeholders in the modified content
  const { processedHtml, newPlaceholders, displayNames } = processUserAddedContent(sanitizedHtml);
  
  // Combine existing and new placeholders
  const allPlaceholders = [...new Set([...existingPlaceholders, ...newPlaceholders])];
  const allDisplayNames = { ...existingDisplayNames, ...displayNames };
  
  return {
    processedHtml,
    allPlaceholders,
    allDisplayNames
  };
};

export const updatePlaceholders = (
  html: string | ProcessedHtmlObject | null, 
  values: Record<string, string>
): string => {
  if (!html) return 'No content to preview';

  let content: string;
  if (typeof html === 'object' && '_type' in html) {
    content = html.value;
  } else if (typeof html === 'string') {
    content = html;
  } else {
    return 'Invalid content format';
  }

  // Parse the HTML content
  const parser = new DOMParser();
  const doc = parser.parseFromString(content, 'text/html');
  
  // Update each placeholder input field
  doc.querySelectorAll('.placeholder-input').forEach(input => {
    const inputElement = input as HTMLInputElement;
    const key = inputElement.getAttribute('data-placeholder');
    if (key && key in values) {
      const value = values[key];
      inputElement.value = value || '';
    }
  });
  
  // Update each placeholder span (for backward compatibility)
  doc.querySelectorAll('.placeholder').forEach(placeholder => {
    const field = placeholder as HTMLSpanElement;
    const key = field.getAttribute('data-placeholder');
    if (key && key in values) {
      const value = values[key];
      field.textContent = value || field.getAttribute('data-label') || '';
    }
  });

  // Extract the body content if it exists, otherwise use the entire HTML
  const bodyContent = doc.body ? doc.body.innerHTML : doc.documentElement.innerHTML;

  // Only add document styling if not already present
  if (!bodyContent.includes('<style>')) {
    return `
      <style>
        .template-content {
          font-family: Arial, sans-serif;
          line-height: 1.6;
          padding: 1in !important;
        }
        .template-content p { margin-bottom: 1em; }
        .template-content h1, h2, h3 { margin: 1em 0 0.5em; }
        .template-content table { 
          width: 100%; 
          border-collapse: collapse; 
          margin: 1em 0;
          position: relative;
        }
        .template-content td, .template-content th { 
          border: 1px solid #ddd; 
          padding: 8px; 
          text-align: left;
          vertical-align: top;
        }
        .placeholder {
          display: inline-block;
          min-width: 120px;
          padding: 2px 6px;
          border-radius: 4px;
          background-color: #FFBF00;
          color: #9ca3af;
          font-style: italic;
          transition: background-color 0.2s;
          text-align: center;
        }
        .placeholder-input {
          min-width: 120px;
          padding: 2px 6px;
          border-radius: 4px;
          background-color: #FFBF00;
          color: #000 !important;
          font-style: italic;
          border: 1px solid #e5e7eb;
          text-align: center;
          transition: background-color 0.2s;
        }
        .placeholder-input::placeholder {
          color: #9ca3af;
          text-align: center;
          font-style: italic;
        }
      </style>
      <div class="template-content">
        ${bodyContent}
      </div>
    `;
  }

  return `
    <div class="template-content">
      ${bodyContent}
    </div>
  `;
};
